var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { addMinutes } from 'date-fns/addMinutes';
import { endOfToday } from 'date-fns/endOfToday';
import { format } from 'date-fns/format';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { isEqual } from 'date-fns/isEqual';
import { isSameHour } from 'date-fns/isSameHour';
import { isSameMinute } from 'date-fns/isSameMinute';
import { enUS } from 'date-fns/locale';
import { setDefaultOptions } from 'date-fns/setDefaultOptions';
import { setHours } from 'date-fns/setHours';
import { setMilliseconds } from 'date-fns/setMilliseconds';
import { setMinutes } from 'date-fns/setMinutes';
import { setSeconds } from 'date-fns/setSeconds';
import { startOfToday } from 'date-fns/startOfToday';
import { html } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { KEYS } from '../../util/event-keys';
import { scrollIntoView } from '../../util/scroll';
import { BlocksElement } from '../BlocksElement';
import styles from './time-selector-list.styles';
/**
 * @fires bl-time-change -Emit when time value change
 * @csspart base - The component's base wrapper.
 * @csspart top - The component's top wrapper.
 * @csspart heading - The component's heading.
 * @csspart inner - The component's inner wrapper.
 * @csspart list - The component's list element.
 * @csspart item - The component's item.
 * @csspart button - The component's `<button>`.

 */
export class BlocksTimeSelectorList extends BlocksElement {
    constructor() {
        super(...arguments);
        /**
         * locale object from date-fns/locale
         */
        this.locale = enUS;
        /**
         * Current Month/Year
         */
        this.currentDate = new Date();
        /**
         * Container for current times in month
         */
        this.times = [];
        /**
         * Increments of time
         */
        this.timeIncrements = 30;
        /**
         * Make active time selector time inactive
         */
        this.resetTime = false;
        /**
         * Use 24 hour time format
         */
        this.is24HourFormat = false;
        /**
         * Specify date format for UI display
         */
        this.timeFormat = 'h:mm a';
        /**
         * Orientation
         * - **horizontal** renders a list where the items are displayed in a row rather than a stacked column.
         */
        this.orientation = undefined;
        /**
         * Time selector heading label
         */
        this.timeSelectorLabel = 'Time';
        /**
         * @fire bl-time-change
         * Change output binding
         */
        this.onChanged = () => {
            if (this.selectedTime) {
                this.dispatch({
                    eventName: 'bl-time-change',
                    detailObj: { value: format(this.selectedTime, this.timeFormat), rawDate: this.selectedTime }
                });
                return format(this.selectedTime, this.timeFormat);
            }
        };
        /**
         * Create times for time selector list
         * 1) Store all display times for month
         * 2) Get all the times from the start to the day of the monday
         * 3) If start and end times are set, only loop through those allowed times
         * 4) Check to see if we need to position the time list based on selected time
         */
        this.setTimes = () => {
            /* 1 */
            this.times = [];
            let todayStart = startOfToday();
            const todayEnd = endOfToday();
            /* 2 */
            while (todayStart <= todayEnd) {
                const time = {
                    value: new Date(),
                    available: true
                };
                time.value = todayStart;
                time.available = true;
                /* 3 */
                if (this.timeStart && this.timeEnd) {
                    const start = this.setTime(this.timeStart);
                    const end = this.setTime(this.timeEnd);
                    if (start && (isAfter(todayStart, start) || isEqual(todayStart, start)) && end && (isBefore(todayStart, end) || isEqual(todayStart, end))) {
                        this.times.push(time);
                    }
                }
                else {
                    this.times.push(time);
                }
                if (this.timeIncrements !== undefined) {
                    todayStart = addMinutes(todayStart, this.timeIncrements);
                }
            }
            /* 4 */
            /** Not sure if we want this functionality?
              const justTimes: Date[] = [];
              this.times.forEach((e) => justTimes.push(e.value));
        
              setTimeout(() => {
                const closestTime = closestTo(this.currentDate, justTimes);
                console.log(justTimes);
        
                this.shadowRoot.querySelector('#cat-time-' + format(closestTime, 'hmma')).scrollIntoView();
              }, 1);
            */
        };
        /**
         * Select the time and emit
         * 1) Bind to form control
         * @param time selected time
         */
        this.selectTime = (time) => {
            // Clear time reset
            this.resetTime = false;
            // Reset the previous focus value
            if (time.value !== this.selectedTime) {
                this.resetFocus();
            }
            if (time.available) {
                this.selectedTime = time.value;
                this.onChanged();
            }
        };
        /**
         * Focus the time elements on arrow up and down keys
         */
        this.handleKeypress = (e) => {
            if (!e) {
                return;
            }
            if (e.key === KEYS.DOWN_ARROW) {
                this.getNextTime(e);
            }
            if (e.key === KEYS.UP_ARROW) {
                this.getPreviousTime(e);
            }
        };
        /**
         * Set the tabindex
         */
        this.setTabValue = (index, time) => {
            if (this.setActiveTime && this.isActiveTime(time)) {
                this._isAvailableEL?.setAttribute('tabindex', '-1');
                return '0';
            }
            if (!this.setActiveTime && index === 0) {
                return '0';
            }
            return '-1';
        };
    }
    static { this.styles = styles; }
    /**
     * First updated lifecycle when component is first updated on page
     */
    firstUpdated() {
        this.setTimes();
        /**
         * Check of a 24 hour time format is set
         */
        if (this.is24HourFormat) {
            this.timeFormat = 'HH:mm';
        }
    }
    async updated(changedProperties) {
        if (changedProperties.has('locale')) {
            const locale = this.locale;
            setDefaultOptions({ locale });
            this.setTimes();
        }
        if (changedProperties.has('setActiveTime')) {
            if (this.setActiveTime) {
                await this.updateComplete;
                const splitHour = this.setActiveTime.split(':')[0];
                const splitMinute = this.setActiveTime.split(':')[1];
                const setActiveHours = setHours(new Date(), parseInt(splitHour, 10));
                const setActiveSeconds = setSeconds(setActiveHours, 0);
                this.selectedTime = setMinutes(setActiveSeconds, parseInt(splitMinute, 10));
                /* Focus on active time element */
                this.setFocusOnTime(this._isActiveEL);
            }
            else {
                this.resetFocus();
            }
        }
        /* Set the initial focus details */
        this.setInitialTime(this.focusedTime);
        if (changedProperties.has('isActive') && !this.isActive && this.selectedTime) {
            this.resetFocus();
        }
    }
    /**
     * Set the time with correct hours and minutes
     */
    setTime(time) {
        if (!time) {
            return;
        }
        let updatedTime;
        const [splitTimeHour = '0', splitTimeMinutes = '0'] = time.split(':');
        // in case the hour or minutes is incorrect return
        if (isNaN(+splitTimeHour) || (splitTimeMinutes && isNaN(+splitTimeMinutes))) {
            return;
        }
        const hours = parseInt(splitTimeHour, 10);
        const minutes = parseInt(splitTimeMinutes, 10);
        updatedTime = setHours(startOfToday(), hours);
        updatedTime = setMinutes(updatedTime, minutes);
        updatedTime = setSeconds(updatedTime, 0);
        return updatedTime;
    }
    /**
     * Check if time can be selected
     * @param time selected time
     */
    isAvailable(time) {
        const dateToCheck = time.value;
        // Min time parsing
        let setActiveDisabledMinTime;
        if (this.disabledMinTime) {
            const splitMinHour = parseInt(this.disabledMinTime.split(':')[0], 10);
            const splitMinMinute = parseInt(this.disabledMinTime.split(':')[1], 10);
            const setActiveMinHours = setSeconds(setHours(new Date(), splitMinHour), 0);
            setActiveDisabledMinTime = setMilliseconds(setMinutes(setActiveMinHours, splitMinMinute), 0);
        }
        // Max time parsing
        let setActiveDisabledMaxTime;
        if (this.disabledMaxTime) {
            const splitMaxHour = parseInt(this.disabledMaxTime.split(':')[0], 10);
            const splitMaxMinute = parseInt(this.disabledMaxTime.split(':')[1], 10);
            const setActiveMaxHours = setSeconds(setHours(new Date(), splitMaxHour), 0);
            setActiveDisabledMaxTime = setMilliseconds(setMinutes(setActiveMaxHours, splitMaxMinute), 0);
        }
        if ((setActiveDisabledMinTime && isAfter(setActiveDisabledMinTime, dateToCheck)) ||
            (setActiveDisabledMaxTime && isBefore(setActiveDisabledMaxTime, dateToCheck))) {
            return false;
        }
        return true;
    }
    /**
     * Apply active state if time has been selected
     * @param time selected time
     */
    isActiveTime(time) {
        let activeTime;
        if (this.setActiveTime) {
            const splitHour = this.setActiveTime.split(':')[0];
            const splitMinute = this.setActiveTime.split(':')[1];
            const setActiveHours = setHours(new Date(), parseInt(splitHour, 10));
            const setActiveSeconds = setSeconds(setActiveHours, 0);
            activeTime = setMinutes(setActiveSeconds, parseInt(splitMinute, 10));
        }
        /**
         * If a specific time is set, highlight it
         */
        if (time.available && this.selectedTime && this.resetTime === false) {
            return isSameHour(time.value, this.selectedTime) && isSameMinute(time.value, this.selectedTime);
        }
        else if (activeTime && isSameHour(time.value, activeTime) && isSameMinute(time.value, activeTime)) {
            return true;
        }
    }
    /**
     * Set the focus on time element
     */
    setFocusOnTime(element, e, isFocus = true) {
        e?.preventDefault();
        this.focusedTime?.setAttribute('tabindex', '-1');
        this.focusedTime = element;
        if (this._timeListContainer && this.focusedTime) {
            scrollIntoView(this.focusedTime, this._timeListContainer, 4, 'vertical', 'smooth');
        }
        isFocus && this.focusedTime?.focus();
        this.focusedTime?.setAttribute('tabindex', '0');
    }
    /**
     * Reset the focus on time
     */
    resetFocus() {
        this.focusedTime?.setAttribute('tabindex', '-1');
        this.focusedTime = null;
    }
    /**
     * Reset the focus on time to first available item
     * 1. Set the focused time as active element else the available element
     */
    resetFocusToFirstTime(isToClearWithTime = true) {
        // If isToClearWithTime is true by default will clear the current focus data else will not clear the existing data if already focused time is available
        if (!isToClearWithTime && this.focusedTime) {
            return;
        }
        this.resetFocus();
        this.focusedTime = this._isActiveEL || this._isAvailableEL; /* 1 */
        this.focusedTime?.setAttribute('tabindex', '0');
    }
    /**
     * 1. Focus to the current focused item
     * 2. If no current focus then focus on the available time
     */
    setFocussedTime() {
        if (this.focusedTime) {
            this.focusedTime?.focus();
            return;
        }
        this.setInitialTime();
    }
    /**
     * Focus the time on fist time
     */
    setInitialTime(focusTime) {
        if (focusTime) {
            return;
        }
        if (this._isActiveEL) {
            this.setFocusOnTime(this._isActiveEL);
            return;
        }
        if (this._isAvailableEL) {
            this.setFocusOnTime(this._isAvailableEL);
        }
    }
    /*
     * Get the below available time when arrow down key is pressed
     */
    getNextTime(e) {
        const closestListItem = this.focusedTime?.closest('.cat-c-time-selector-list__item');
        const nextAvailableTime = closestListItem?.nextElementSibling?.querySelector('.is-available');
        if (nextAvailableTime) {
            this.setFocusOnTime(nextAvailableTime, e);
            return;
        }
        const closestList = this.focusedTime?.closest('.cat-c-time-selector-list__list');
        const firstAvailableTime = closestList?.querySelector('.is-available');
        if (firstAvailableTime) {
            this.setFocusOnTime(firstAvailableTime, e);
        }
    }
    /*
     * Get the above available time when arrow up key is pressed
     */
    getPreviousTime(e) {
        const closestListItem = this.focusedTime?.closest('.cat-c-time-selector-list__item');
        const previousAvailableTime = closestListItem?.previousElementSibling?.querySelector('.is-available');
        if (previousAvailableTime) {
            this.setFocusOnTime(previousAvailableTime, e);
            return;
        }
        const closestList = this.focusedTime?.closest('.cat-c-time-selector-list__list');
        const lastAvailableTime = Array.from(closestList?.querySelectorAll('.is-available') || []).pop();
        if (lastAvailableTime) {
            this.setFocusOnTime(lastAvailableTime, e);
        }
    }
    render() {
        const componentClassNames = this.componentClassNames('cat-c-time-selector-list', {
            'cat-c-time-selector-list--horizontal': this.orientation === 'horizontal'
        });
        return html `
      <div part="base" class="${componentClassNames}" @keydown=${this.handleKeypress}>
        <div part="top" class="cat-c-time-selector-list__top">
          <h4 part="heading" class="cat-c-time-selector-list__heading">${this.timeSelectorLabel}</h4>
        </div>
        <div part="inner" class="cat-c-time-selector-list__inner">
          <ul part="list" class="cat-c-time-selector-list__list">
            ${this.times.map((time, index) => {
            return html `
                <li part="item" class="cat-c-time-selector-list__item" id="cat-time-${format(time.value, 'hmma')}">
                  ${time.value.getTime() !== 0
                ? html `
                        <button
                          part="button"
                          class="cat-c-time-selector-list__button
                          ${this.isActiveTime(time) ? 'is-active' : ''}
                          ${this.isAvailable(time) ? 'is-available' : ''}"
                          aria-pressed=${ifDefined(this.isActiveTime(time) ? 'true' : undefined)}
                          @click=${() => this.selectTime(time)}
                          ?disabled=${!this.isAvailable(time)}
                          aria-label="${format(time.value, this.timeFormat)}"
                          tabindex=${this.setTabValue(index, time)}
                        >
                          ${format(time.value, this.timeFormat)}
                        </button>
                      `
                : ''}
                </li>
              `;
        })}
          </ul>
        </div>
      </div>
    `;
    }
}
__decorate([
    property({ type: Object })
], BlocksTimeSelectorList.prototype, "locale", void 0);
__decorate([
    property({ type: Object })
], BlocksTimeSelectorList.prototype, "currentDate", void 0);
__decorate([
    property({ type: String })
], BlocksTimeSelectorList.prototype, "setActiveTime", void 0);
__decorate([
    property({ type: Array })
], BlocksTimeSelectorList.prototype, "times", void 0);
__decorate([
    property({ type: Number })
], BlocksTimeSelectorList.prototype, "timeIncrements", void 0);
__decorate([
    property({ type: String })
], BlocksTimeSelectorList.prototype, "timeStart", void 0);
__decorate([
    property({ type: String })
], BlocksTimeSelectorList.prototype, "timeEnd", void 0);
__decorate([
    property({ type: String })
], BlocksTimeSelectorList.prototype, "disabledMinTime", void 0);
__decorate([
    property({ type: String })
], BlocksTimeSelectorList.prototype, "disabledMaxTime", void 0);
__decorate([
    property({ type: Boolean })
], BlocksTimeSelectorList.prototype, "resetTime", void 0);
__decorate([
    property({ type: Boolean })
], BlocksTimeSelectorList.prototype, "is24HourFormat", void 0);
__decorate([
    property({ type: String })
], BlocksTimeSelectorList.prototype, "timeFormat", void 0);
__decorate([
    property({ type: String })
], BlocksTimeSelectorList.prototype, "orientation", void 0);
__decorate([
    property({ type: String })
], BlocksTimeSelectorList.prototype, "timeSelectorLabel", void 0);
__decorate([
    property({ type: Boolean })
], BlocksTimeSelectorList.prototype, "isActive", void 0);
__decorate([
    query('.is-active')
], BlocksTimeSelectorList.prototype, "_isActiveEL", void 0);
__decorate([
    query('.is-available')
], BlocksTimeSelectorList.prototype, "_isAvailableEL", void 0);
__decorate([
    query('.cat-c-time-selector-list__inner')
], BlocksTimeSelectorList.prototype, "_timeListContainer", void 0);
__decorate([
    state()
], BlocksTimeSelectorList.prototype, "selectedTime", void 0);
if (customElements.get('cat-time-selector-list') === undefined) {
    customElements.define('cat-time-selector-list', BlocksTimeSelectorList);
}
